import {
  Buildings,
  ClipboardText,
  DesktopTower,
  GearSix,
  HardDrives,
  MapTrifold,
  SquaresFour,
  WarningCircle,
} from "phosphor-solid";
import type { WorkspacePageMeta } from "@/lib/workspace-page";
import { canonicalPathForNav } from "@/lib/navigation";

type RouteInfo = Omit<WorkspacePageMeta, "pathname">;

function baseCrumbs(section: string, href: string) {
  return [{ label: section, href }];
}

export function getWorkspaceRouteInfo(pathname: string): RouteInfo {
  const path = canonicalPathForNav(pathname);

  if (path === "/app") {
    return {
      section: "Monitor",
      title: "Workspace",
      description: "Your tenant workspace and available operating areas.",
      icon: SquaresFour,
      breadcrumbs: [{ label: "Workspace", href: "/app" }],
    };
  }

  if (path === "/dashboard") {
    return {
      section: "Monitor",
      title: "Dashboard",
      description: "System overview for health, risk, alerts, and work.",
      icon: SquaresFour,
      breadcrumbs: baseCrumbs("Monitor", "/dashboard"),
    };
  }

  if (path === "/dashboard/map") {
    return {
      section: "Investigate",
      title: "Map",
      description: "Spatial view of sites, assets, and operational focus.",
      icon: MapTrifold,
      breadcrumbs: [
        { label: "Monitor", href: "/dashboard" },
        { label: "Map", href: "/dashboard/map" },
      ],
    };
  }

  if (path === "/alerts") {
    return {
      section: "Triage",
      title: "Alerts",
      description: "Active and historical operational alerts.",
      icon: WarningCircle,
      breadcrumbs: baseCrumbs("Triage", "/alerts"),
    };
  }

  if (path.startsWith("/alerts/")) {
    return {
      section: "Triage",
      title: "Alert detail",
      description: "Alert context, affected entity, and investigation links.",
      icon: WarningCircle,
      breadcrumbs: [
        { label: "Triage", href: "/alerts" },
        { label: "Alerts", href: "/alerts" },
        { label: "Alert detail" },
      ],
    };
  }

  if (path === "/work") {
    return {
      section: "Triage",
      title: "Work",
      description: "Open and resolved follow-up work.",
      icon: ClipboardText,
      breadcrumbs: baseCrumbs("Triage", "/work"),
    };
  }

  if (path.startsWith("/work/")) {
    return {
      section: "Triage",
      title: "Work item detail",
      description: "Assignment, affected entity, and linked alert context.",
      icon: ClipboardText,
      breadcrumbs: [
        { label: "Triage", href: "/work" },
        { label: "Work", href: "/work" },
        { label: "Work item detail" },
      ],
    };
  }

  if (path === "/registry" || path.startsWith("/registry/")) {
    if (path === "/registry/sites") {
      return registryInfo("Sites", "Maintain sites and location context.", Buildings, [
        { label: "Assets", href: "/registry" },
        { label: "Sites", href: "/registry/sites" },
      ]);
    }
    if (path.startsWith("/registry/sites/")) {
      return registryInfo("Site detail", "Site health, assets, and configuration.", Buildings, [
        { label: "Assets", href: "/registry" },
        { label: "Sites", href: "/registry/sites" },
        { label: "Site detail" },
      ]);
    }
    if (path === "/registry/assets") {
      return registryInfo("Assets", "Maintain assets and operational state.", HardDrives, [
        { label: "Assets", href: "/registry" },
        { label: "Asset inventory", href: "/registry/assets" },
      ]);
    }
    if (path.startsWith("/registry/assets/")) {
      return registryInfo("Asset detail", "Asset health, operations, assignments, and configuration.", HardDrives, [
        { label: "Assets", href: "/registry" },
        { label: "Asset inventory", href: "/registry/assets" },
        { label: "Asset detail" },
      ]);
    }
    if (path === "/registry/devices") {
      return registryInfo("Devices", "Maintain devices, connectivity, and identity.", DesktopTower, [
        { label: "Assets", href: "/registry" },
        { label: "Device inventory", href: "/registry/devices" },
      ]);
    }
    if (path.startsWith("/registry/devices/")) {
      return registryInfo("Device detail", "Device health, assignments, identity, and configuration.", DesktopTower, [
        { label: "Assets", href: "/registry" },
        { label: "Device inventory", href: "/registry/devices" },
        { label: "Device detail" },
      ]);
    }
    return registryInfo("Registry", "Sites, assets, and devices in the monitored system.", Buildings, [
      { label: "Assets", href: "/registry" },
    ]);
  }

  if (path === "/integrations") {
    return {
      section: "Setup",
      title: "Integrations",
      description: "Integration health, mappings, and simulator controls.",
      icon: GearSix,
      breadcrumbs: [{ label: "Setup", href: "/integrations" }],
    };
  }

  if (pathname.startsWith("/app/admin")) {
    return {
      section: "Admin",
      title: "Admin",
      description: "Organization, tenants, users, and audit controls.",
      icon: GearSix,
      breadcrumbs: [{ label: "Admin", href: "/app/admin" }],
    };
  }

  return {
    section: "Workspace",
    title: "Not found",
    description: "This workspace route does not exist.",
    icon: SquaresFour,
    breadcrumbs: [{ label: "Workspace", href: "/app" }, { label: "Not found" }],
  };
}

function registryInfo(
  title: string,
  description: string,
  icon: RouteInfo["icon"],
  breadcrumbs: RouteInfo["breadcrumbs"],
): RouteInfo {
  return {
    section: "Assets",
    title,
    description,
    icon,
    breadcrumbs,
  };
}

export function workspaceDocumentTitle(meta: Pick<WorkspacePageMeta, "title" | "section">) {
  return `${meta.title} · ${meta.section} · Conatus`;
}

